import React, { useEffect, useState } from 'react';
import { Card, Button, Form, Row, Col, Table, Badge, Modal, Alert, Spinner, InputGroup } from 'react-bootstrap';
import { Helmet } from 'react-helmet-async';
import toast from 'react-hot-toast';
import adminService from '../../services/adminService';

interface AdminUser {
  id: string;
  email: string;
  name: string;
  role: string;
  permissions: string[];
  is_active: boolean;
  last_login_at?: string;
  created_at: string;
  updated_at: string;
}

interface Role {
  value: string;
  label: string;
  description: string;
}

interface Permission {
  value: string;
  label: string;
  description: string;
}

const AdminUsers: React.FC = () => {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState<AdminUser | null>(null);
  const [roles, setRoles] = useState<Role[]>([]);
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    role: 'staff',
    permissions: [] as string[],
    is_active: true,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');

  useEffect(() => {
    fetchUsers();
    fetchRoles();
    fetchPermissions();
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [searchTerm, filterRole, filterStatus]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await adminService.getAdminUsers({
        search: searchTerm || undefined,
        role: filterRole !== 'all' ? filterRole : undefined,
        is_active: filterStatus !== 'all' ? filterStatus === 'active' : undefined,
      });
      if (response.success && response.data) {
        // The API returns { users: AdminUser[], pagination: {...} }
        setUsers(response.data.users || []);
      } else {
        toast.error(response.error || 'Failed to load admin users');
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to load admin users');
    } finally {
      setLoading(false);
    }
  };

  const fetchRoles = async () => {
    try {
      const response = await adminService.getAdminUserRoles();
      if (response.success && response.data) {
        setRoles(response.data);
      }
    } catch (error: any) {
      console.error('Failed to load roles:', error);
    }
  };

  const fetchPermissions = async () => {
    try {
      const response = await adminService.getAdminUserPermissions();
      if (response.success && response.data) {
        setPermissions(response.data);
      }
    } catch (error: any) {
      console.error('Failed to load permissions:', error);
    }
  };

  const getDefaultPermissionsForRole = (role: string): string[] => {
    switch (role) {
      case 'superadmin':
        return ['*']; // All permissions
      case 'admin':
        return [
          'users:read',
          'users:write',
          'bookings:read',
          'bookings:write',
          'movies:read',
          'movies:write',
          'cinemas:read',
          'cinemas:write',
          'cms:read',
          'cms:write',
          'notifications:read',
          'notifications:write',
          'settings:read',
          'settings:write',
          'coupons:read',
          'coupons:write',
          'analytics:read',
        ];
      case 'manager':
        return [
          'bookings:read',
          'bookings:write',
          'movies:read',
          'movies:write',
          'cinemas:read',
          'cinemas:write',
          'cms:read',
          'cms:write',
          'notifications:read',
          'notifications:write',
          'coupons:read',
          'coupons:write',
          'analytics:read',
        ];
      case 'staff':
        return [
          'bookings:read',
          'bookings:write',
          'movies:read',
          'cinemas:read',
          'notifications:read'
        ];
      default:
        return [];
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }

    if (!editingUser && !formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password && formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    }

    if (!formData.name) {
      newErrors.name = 'Name is required';
    }

    if (!formData.role) {
      newErrors.role = 'Role is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleOpenModal = (user?: AdminUser) => {
    if (user) {
      setEditingUser(user);
      setFormData({
        email: user.email,
        password: '',
        name: user.name,
        role: user.role,
        permissions: Array.isArray(user.permissions) ? user.permissions : [],
        is_active: user.is_active,
      });
    } else {
      setEditingUser(null);
      const defaultRole = 'staff';
      setFormData({
        email: '',
        password: '',
        name: '',
        role: defaultRole,
        permissions: getDefaultPermissionsForRole(defaultRole),
        is_active: true,
      });
    }
    setErrors({});
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingUser(null);
    const defaultRole = 'staff';
    setFormData({
      email: '',
      password: '',
      name: '',
      role: defaultRole,
      permissions: getDefaultPermissionsForRole(defaultRole),
      is_active: true,
    });
    setErrors({});
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => {
      const newData = {
        ...prev,
        [field]: value,
      };
      
      // If role is changed, update permissions automatically
      if (field === 'role') {
        newData.permissions = getDefaultPermissionsForRole(value);
      }
      
      return newData;
    });
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handlePermissionToggle = (permissionValue: string) => {
    setFormData(prev => {
      const currentPermissions = prev.permissions || [];
      if (currentPermissions.includes('*')) {
        // If "*" is selected, remove it and add this permission
        return {
          ...prev,
          permissions: [permissionValue],
        };
      }
      if (permissionValue === '*') {
        // If selecting "*", replace all permissions
        return {
          ...prev,
          permissions: ['*'],
        };
      }
      if (currentPermissions.includes(permissionValue)) {
        return {
          ...prev,
          permissions: currentPermissions.filter(p => p !== permissionValue),
        };
      }
      return {
        ...prev,
        permissions: [...currentPermissions, permissionValue],
      };
    });
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      setSaving(true);
      const userData: any = {
        email: formData.email,
        name: formData.name,
        role: formData.role,
        permissions: formData.permissions,
        is_active: formData.is_active,
      };

      // Only include password if it's provided (for new users or password updates)
      if (formData.password) {
        userData.password = formData.password;
      }

      if (editingUser) {
        const response = await adminService.updateAdminUser(editingUser.id, userData);
        if (response.success) {
          toast.success('Admin user updated successfully');
          fetchUsers();
          handleCloseModal();
        } else {
          toast.error(response.error || 'Failed to update admin user');
        }
      } else {
        if (!formData.password) {
          toast.error('Password is required for new users');
          return;
        }
        const response = await adminService.createAdminUser(userData);
        if (response.success) {
          toast.success('Admin user created successfully');
          fetchUsers();
          handleCloseModal();
        } else {
          toast.error(response.error || 'Failed to create admin user');
        }
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to save admin user');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string, name: string) => {
    if (!window.confirm(`Are you sure you want to delete admin user "${name}"?`)) {
      return;
    }

    try {
      const response = await adminService.deleteAdminUser(id);
      if (response.success) {
        toast.success('Admin user deleted successfully');
        fetchUsers();
      } else {
        toast.error(response.error || 'Failed to delete admin user');
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to delete admin user');
    }
  };

  const handleToggleActive = async (user: AdminUser) => {
    try {
      const response = await adminService.updateAdminUser(user.id, {
        ...user,
        is_active: !user.is_active,
      });
      if (response.success) {
        toast.success(`Admin user ${!user.is_active ? 'activated' : 'deactivated'} successfully`);
        fetchUsers();
      } else {
        toast.error(response.error || 'Failed to update admin user');
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to update admin user');
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'Never';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return dateString;
    }
  };

  return (
    <>
      <Helmet>
        <title>Admin Users - MyCinemas Admin</title>
      </Helmet>

      <div className="container-fluid py-4">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h2>Admin Users</h2>
          <Button variant="primary" onClick={() => handleOpenModal()}>
            <i className="bi bi-plus-circle me-2"></i>
            Add Admin User
          </Button>
        </div>

        <Card>
          <Card.Body>
            {/* Filters */}
            <Row className="mb-3">
              <Col md={4}>
                <InputGroup>
                  <InputGroup.Text>
                    <i className="bi bi-search"></i>
                  </InputGroup.Text>
                  <Form.Control
                    type="text"
                    placeholder="Search by email or name..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </InputGroup>
              </Col>
              <Col md={3}>
                <Form.Select
                  value={filterRole}
                  onChange={(e) => setFilterRole(e.target.value)}
                >
                  <option value="all">All Roles</option>
                  {roles.map(role => (
                    <option key={role.value} value={role.value}>{role.label}</option>
                  ))}
                </Form.Select>
              </Col>
              <Col md={3}>
                <Form.Select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                >
                  <option value="all">All Status</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </Form.Select>
              </Col>
              <Col md={2}>
                <Button variant="outline-secondary" onClick={fetchUsers}>
                  <i className="bi bi-arrow-clockwise"></i> Refresh
                </Button>
              </Col>
            </Row>

            {loading ? (
              <div className="text-center py-5">
                <Spinner animation="border" variant="primary" />
                <p className="mt-3">Loading admin users...</p>
              </div>
            ) : users.length === 0 ? (
              <div className="text-center py-5">
                <p className="text-muted">No admin users found.</p>
              </div>
            ) : (
              <Table responsive striped hover>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Role</th>
                    <th>Permissions</th>
                    <th>Last Login</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user) => (
                    <tr key={user.id}>
                      <td>{user.name}</td>
                      <td>{user.email}</td>
                      <td>
                        <Badge bg="info">{user.role}</Badge>
                      </td>
                      <td>
                        {Array.isArray(user.permissions) && user.permissions.includes('*') ? (
                          <Badge bg="success">All Permissions</Badge>
                        ) : (
                          <Badge bg="secondary">
                            {Array.isArray(user.permissions) ? user.permissions.length : 0} permission(s)
                          </Badge>
                        )}
                      </td>
                      <td>{formatDate(user.last_login_at || '')}</td>
                      <td>
                        <Badge bg={user.is_active ? 'success' : 'secondary'}>
                          {user.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </td>
                      <td>
                        <Button
                          variant="link"
                          size="sm"
                          onClick={() => handleOpenModal(user)}
                          className="me-2"
                        >
                          <i className="bi bi-pencil"></i> Edit
                        </Button>
                        <Button
                          variant="link"
                          size="sm"
                          onClick={() => handleToggleActive(user)}
                          className="me-2"
                        >
                          <i className={`bi bi-toggle-${user.is_active ? 'on' : 'off'}`}></i>{' '}
                          {user.is_active ? 'Deactivate' : 'Activate'}
                        </Button>
                        <Button
                          variant="link"
                          size="sm"
                          className="text-danger"
                          onClick={() => handleDelete(user.id, user.name)}
                        >
                          <i className="bi bi-trash"></i> Delete
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </Card.Body>
        </Card>
      </div>

      {/* Add/Edit Modal */}
      <Modal show={showModal} onHide={handleCloseModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {editingUser ? 'Edit Admin User' : 'Add New Admin User'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Name *</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    isInvalid={!!errors.name}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.name}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Email *</Form.Label>
                  <Form.Control
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    isInvalid={!!errors.email}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.email}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Password {editingUser ? '(leave blank to keep current)' : '*'}</Form.Label>
                  <Form.Control
                    type="password"
                    value={formData.password}
                    onChange={(e) => handleInputChange('password', e.target.value)}
                    isInvalid={!!errors.password}
                    placeholder={editingUser ? 'Enter new password to change' : 'Minimum 8 characters'}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.password}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Role *</Form.Label>
                  <Form.Select
                    value={formData.role}
                    onChange={(e) => handleInputChange('role', e.target.value)}
                    isInvalid={!!errors.role}
                  >
                    {roles.map(role => (
                      <option key={role.value} value={role.value}>
                        {role.label} - {role.description}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Control.Feedback type="invalid">
                    {errors.role}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>
            <Form.Group className="mb-3">
              <Form.Label>Permissions</Form.Label>
              <div style={{ maxHeight: '200px', overflowY: 'auto', border: '1px solid #dee2e6', borderRadius: '4px', padding: '10px' }}>
                {permissions.map(permission => (
                  <Form.Check
                    key={permission.value}
                    type="checkbox"
                    id={`permission-${permission.value}`}
                    label={
                      <div>
                        <strong>{permission.label}</strong>
                        <br />
                        <small className="text-muted">{permission.description}</small>
                      </div>
                    }
                    checked={formData.permissions.includes(permission.value)}
                    onChange={() => handlePermissionToggle(permission.value)}
                  />
                ))}
              </div>
              <Form.Text className="text-muted">
                Select specific permissions or choose "All Permissions" for full access
              </Form.Text>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Check
                type="switch"
                id="is_active"
                label="Active"
                checked={formData.is_active}
                onChange={(e) => handleInputChange('is_active', e.target.checked)}
              />
              <Form.Text className="text-muted">
                Inactive users cannot log in
              </Form.Text>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseModal}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSave} disabled={saving}>
            {saving ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Saving...
              </>
            ) : (
              'Save'
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default AdminUsers;

